/**
 * Certificate type registry.
 *
 * Each entry defines:
 *   - key: URL slug + backend type value
 *   - label: chip / heading text
 *   - sections: an ordered list of form sections; each section has an array of
 *     fields with {name, label, type, placeholder, cols, source}.
 *       type: text | textarea | date | select | number
 *       cols: 1|2|3 (grid col span)
 *       source (optional): auto-prefill hint — 'vitals.sbp', 'labs.hb', etc.
 *
 * The PrintCertificate page renders sections in the same order using the
 * stored `fields` blob — so any new field added here will show up on print
 * without extra work.
 */
export const CERT_TYPES = {
  preop: {
    key: "preop",
    label: "Pre-Operative Fitness",
    intro: (p) => `Thanks for referring ${p.name || "the patient"}, ${p.age || "—"} years, ${p.sex || "—"} for fitness.`,
    sections: [
      { title: "Procedure & History", fields: [
        { name: "referring_doctor", label: "Referring doctor", type: "text", cols: 2 },
        { name: "procedure", label: "Posted for (procedure)", type: "text", cols: 2 },
        { name: "known_case_of", label: "K/C/O", type: "textarea", cols: 3, source: "known_case_of" },
      ]},
      { title: "Investigations", fields: [
        { name: "test_date", label: "Test date", type: "date", cols: 1 },
        { name: "hb", label: "Hb (gm%)", type: "text", cols: 1, source: "labs.hb" },
        { name: "tlc", label: "TLC (/cu mm)", type: "text", cols: 1 },
        { name: "platelets", label: "Platelets", type: "text", cols: 1, source: "labs.platelets" },
        { name: "bsl", label: "BSL R (mg%)", type: "text", cols: 1, source: "labs.rbs" },
        { name: "creatinine", label: "Creatinine (mg%)", type: "text", cols: 1, source: "labs.creatinine" },
        { name: "hbsag", label: "HBsAg", type: "text", cols: 1 },
        { name: "hiv", label: "HIV", type: "text", cols: 1 },
        { name: "urine", label: "Urine R/M", type: "text", cols: 1 },
        { name: "blood_group", label: "Blood Group", type: "text", cols: 1 },
      ]},
      { title: "Medications & Allergies", fields: [
        { name: "medications", label: "Ongoing medications", type: "textarea", cols: 3, source: "medicines" },
        { name: "allergy", label: "Allergy to medications (Yes/No)", type: "text", cols: 1 },
        { name: "allergy_drug", label: "Allergy — Drug name", type: "text", cols: 2 },
        { name: "covid_vaccine_year", label: "Last COVID vaccine year", type: "text", cols: 1 },
        { name: "prev_anesthesia", label: "H/O surgery under anaesthesia", type: "text", cols: 2 },
        { name: "hospitalisation", label: "H/O Hospitalisation", type: "text", cols: 3 },
      ]},
      { title: "General & Systemic Examination", fields: [
        { name: "hr", label: "HR (/min)", type: "text", cols: 1, source: "vitals.pulse" },
        { name: "rr", label: "RR (/min)", type: "text", cols: 1, source: "vitals.rr" },
        { name: "bp", label: "BP (mmHg)", type: "text", cols: 1, source: "vitals.bp" },
        { name: "pallor", label: "Pallor", type: "text", cols: 1 },
        { name: "lymphadenopathy", label: "Lymphadenopathy", type: "text", cols: 2 },
        { name: "rs", label: "RS (Respiratory)", type: "text", cols: 1 },
        { name: "cvs", label: "CVS (Cardiovascular)", type: "text", cols: 1 },
        { name: "cns", label: "CNS", type: "text", cols: 1 },
        { name: "pa", label: "P/A (Abdomen)", type: "text", cols: 1 },
        { name: "ecg", label: "ECG", type: "text", cols: 2 },
      ]},
      { title: "Advice", fields: [
        { name: "rx", label: "Rx", type: "textarea", cols: 3 },
        { name: "advise", label: "Advise", type: "textarea", cols: 3 },
      ]},
    ],
  },
  medical: {
    key: "medical",
    label: "Medical Certificate",
    intro: (p) => `This is to certify that ${p.name || "the patient"}, ${p.age || "—"} years, ${p.sex || "—"}, was examined by me.`,
    sections: [
      { title: "Diagnosis & Rest", fields: [
        { name: "reason", label: "Reason for consultation", type: "textarea", cols: 3 },
        { name: "diagnosis", label: "Diagnosis", type: "textarea", cols: 3, source: "diagnosis.primary" },
        { name: "rest_from", label: "Rest from", type: "date", cols: 1 },
        { name: "rest_to", label: "Rest till", type: "date", cols: 1 },
        { name: "rest_days", label: "Rest duration (days)", type: "number", cols: 1 },
        { name: "resume_date", label: "Fit to resume on", type: "date", cols: 2 },
        { name: "remarks", label: "Remarks", type: "textarea", cols: 3 },
      ]},
    ],
  },
  preemployment: {
    key: "preemployment",
    label: "Pre-employment Certificate",
    intro: (p) => `${p.name || "The candidate"}, ${p.age || "—"} years, ${p.sex || "—"}, was examined for pre-employment fitness.`,
    sections: [
      { title: "Employment Details", fields: [
        { name: "company", label: "Company / Employer", type: "text", cols: 2 },
        { name: "role", label: "Role applied for", type: "text", cols: 1 },
      ]},
      { title: "General Examination", fields: [
        { name: "height", label: "Height (cm)", type: "text", cols: 1, source: "vitals.height" },
        { name: "weight", label: "Weight (kg)", type: "text", cols: 1, source: "vitals.weight" },
        { name: "bmi", label: "BMI", type: "text", cols: 1, source: "vitals.bmi" },
        { name: "bp", label: "BP (mmHg)", type: "text", cols: 1, source: "vitals.bp" },
        { name: "pulse", label: "Pulse (/min)", type: "text", cols: 1, source: "vitals.pulse" },
        { name: "vision", label: "Vision — normal?", type: "text", cols: 1 },
        { name: "hearing", label: "Hearing — normal?", type: "text", cols: 1 },
        { name: "comorbidities", label: "Known comorbidities", type: "textarea", cols: 3, source: "known_case_of" },
      ]},
      { title: "Systemic Examination", fields: [
        { name: "rs", label: "RS", type: "text", cols: 1 },
        { name: "cvs", label: "CVS", type: "text", cols: 1 },
        { name: "cns", label: "CNS", type: "text", cols: 1 },
        { name: "pa", label: "P/A", type: "text", cols: 1 },
      ]},
      { title: "Fitness Opinion", fields: [
        { name: "fitness", label: "Fitness", type: "select", cols: 1, options: ["Fit", "Fit with restrictions", "Unfit"] },
        { name: "restrictions", label: "Restrictions (if any)", type: "textarea", cols: 3 },
        { name: "remarks", label: "Remarks", type: "textarea", cols: 3 },
      ]},
    ],
  },
  fitness: {
    key: "fitness",
    label: "Fitness Certificate",
    intro: (p) => `This is to certify that ${p.name || "the patient"}, ${p.age || "—"} years, ${p.sex || "—"}, has been medically examined by me.`,
    sections: [
      { title: "Purpose & Assessment", fields: [
        { name: "purpose", label: "Purpose", type: "select", cols: 1, options: ["School / College", "Workplace return", "Sports", "Travel", "Driving licence", "Other"] },
        { name: "purpose_detail", label: "Purpose details", type: "text", cols: 2 },
        { name: "current_complaints", label: "Current complaints", type: "textarea", cols: 3 },
        { name: "bp", label: "BP", type: "text", cols: 1, source: "vitals.bp" },
        { name: "pulse", label: "Pulse", type: "text", cols: 1, source: "vitals.pulse" },
        { name: "fitness_status", label: "Fitness status", type: "select", cols: 1, options: ["Fit to resume", "Not yet fit", "Fit with restrictions"] },
        { name: "resume_date", label: "Fit to resume from", type: "date", cols: 1 },
        { name: "restrictions", label: "Restrictions (if any)", type: "textarea", cols: 2 },
        { name: "remarks", label: "Remarks", type: "textarea", cols: 3 },
      ]},
    ],
  },
};

export const CERT_LIST = Object.values(CERT_TYPES);

/**
 * Pulls a value from a source-hint path on the prefill snapshot.
 * Handles `medicines` array specially — joins name + generic.
 * Handles `labs.<key>` — uses ctx.labs[key].value.
 * Handles `vitals.<key>` — vitals.bp special-cases into "sbp/dbp mmHg".
 */
export function prefillValue(sourceHint, ctx) {
  if (!sourceHint || !ctx) return "";
  if (sourceHint === "medicines") {
    const list = ctx.medicines || [];
    return list.filter((m) => m.brand_name || m.generic_name)
      .map((m) => `${m.brand_name || m.generic_name} ${m.strength || ""} ${m.frequency || ""}`.trim())
      .join(", ");
  }
  if (sourceHint === "known_case_of") {
    return (ctx.known_case_of || []).join(", ");
  }
  if (sourceHint === "diagnosis.primary") {
    return ctx?.diagnosis?.primary || "";
  }
  if (sourceHint.startsWith("labs.")) {
    const k = sourceHint.slice(5);
    return ctx?.labs?.[k]?.value ?? "";
  }
  if (sourceHint.startsWith("vitals.")) {
    const k = sourceHint.slice(7);
    if (k === "bp") {
      const s = ctx?.vitals?.sbp || "";
      const d = ctx?.vitals?.dbp || "";
      return s || d ? `${s}/${d}` : "";
    }
    return ctx?.vitals?.[k] || "";
  }
  return "";
}
